import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Upload, Music, Plus, Loader2, ListMusic, Folder } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuickAddTrackDialog = ({ 
  open, 
  onOpenChange, 
  eventId,
  currentPartId,
  onSuccess 
}) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [destination, setDestination] = useState('queue'); // 'queue' or 'playlist'

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      // Validate file types
      const validFiles = selectedFiles.filter(file => file.type.startsWith('audio/'));
      if (validFiles.length !== selectedFiles.length) {
        toast.warning('Certains fichiers ne sont pas des fichiers audio');
      }
      setFiles(validFiles);
      console.log(`📁 ${validFiles.length} file(s) selected`);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Veuillez sélectionner au moins un fichier');
      return;
    }

    if (!currentPartId) {
      toast.error('Aucune partie active - activez une partie d\'abord');
      return;
    }

    setUploading(true);
    console.log(`📤 Uploading ${files.length} file(s) to ${destination}`);

    try {
      // First, get the part details to find the main playlist
      
      const partResponse = await axios.get(
        `${BACKEND_URL}/api/parts/${currentPartId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const mainPlaylist = partResponse.data.playlists?.find(p => p.type === 'main');
      if (!mainPlaylist) {
        toast.error('Aucune playlist principale trouvée');
        setUploading(false);
        return;
      }

      const playlistId = mainPlaylist.id;
      console.log(`📋 Target playlist: ${playlistId}`);

      // Upload each file
      const uploadedTracks = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`⬆️ Uploading ${i + 1}/${files.length}: ${file.name}`);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('playlist_id', playlistId);
        formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
        formData.append('artist', 'Unknown');
        formData.append('order_index', 999 + i);

        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/tracks/upload`,
            formData,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          uploadedTracks.push(response.data);
          console.log(`✅ Uploaded: ${file.name}`);
        } catch (error) {
          console.error(`❌ Error uploading ${file.name}:`, error);
          toast.error(`Erreur: ${file.name}`);
        }
      }

      if (uploadedTracks.length > 0) {
        console.log(`✅ ${uploadedTracks.length}/${files.length} tracks uploaded successfully`);
        
        // Notify success
        if (destination === 'queue') {
          toast.success(`🎵 ${uploadedTracks.length} titre(s) ajouté(s) à la file !`);
        } else {
          toast.success(`📂 ${uploadedTracks.length} titre(s) ajouté(s) à la playlist !`);
        }

        // Call success callback
        if (onSuccess) {
          onSuccess(uploadedTracks, destination);
        }

        // Reset and close
        setFiles([]);
        setUploading(false);
        onOpenChange(false);
      } else {
        toast.error('Aucun fichier n\'a pu être uploadé');
        setUploading(false);
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      toast.error('Erreur lors de l\'upload');
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-dark-card border-sunset-orange/30 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-3">
            <Plus className="w-6 h-6 text-purple-400" />
            Ajouter des titres rapidement
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Destination Selection */}
          <div className="space-y-4">
            <Label className="text-purple-200">Où ajouter les titres ?</Label>
            
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setDestination('queue')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  destination === 'queue'
                    ? 'border-sunset-orange bg-purple-500/20'
                    : 'border-sunset-orange/30 bg-dark-input/50 hover:border-sunset-orange/50'
                }`}
              >
                <ListMusic className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                <p className="font-semibold text-white">File d'attente</p>
                <p className="text-xs text-sunset-orange/60 mt-1">Joué immédiatement après les titres en cours</p>
              </button>

              <button
                onClick={() => setDestination('playlist')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  destination === 'playlist'
                    ? 'border-sunset-orange bg-purple-500/20'
                    : 'border-sunset-orange/30 bg-dark-input/50 hover:border-sunset-orange/50'
                }`}
              >
                <Folder className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                <p className="font-semibold text-white">Playlist principale</p>
                <p className="text-xs text-sunset-orange/60 mt-1">Ajouté à la partie active pour réutilisation</p>
              </button>
            </div>
          </div>

          {/* File Upload */}
          <div className="space-y-4">
            <Label className="text-purple-200">Fichiers audio</Label>
            
            <div className="border-2 border-dashed border-sunset-orange/30 rounded-lg p-6 text-center hover:border-sunset-orange/50 transition-colors">
              <input
                type="file"
                id="quick-track-upload"
                accept="audio/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="quick-track-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="w-12 h-12 text-purple-400 mb-3" />
                {files.length > 0 ? (
                  <div className="text-center w-full">
                    <p className="text-white font-semibold mb-2">
                      {files.length} fichier{files.length > 1 ? 's' : ''} sélectionné{files.length > 1 ? 's' : ''}
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1 mb-3">
                      {files.map((file, idx) => (
                        <div key={idx} className="text-sm text-sunset-orange/80 flex items-center justify-center gap-2">
                          <Music className="w-3 h-3" />
                          <span className="truncate max-w-md">{file.name}</span>
                          <span className="text-sunset-orange/40">
                            ({(file.size / 1024 / 1024).toFixed(1)} MB)
                          </span>
                        </div>
                      ))}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        setFiles([]);
                      }}
                      className="text-purple-400"
                    >
                      Changer les fichiers
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-white font-semibold mb-1">
                      Cliquez pour sélectionner des fichiers audio
                    </p>
                    <p className="text-sm text-sunset-orange/60">
                      MP3, WAV, FLAC, etc. (sélection multiple possible)
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {files.length > 0 && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-sm text-blue-200">
                ℹ️ Les titres seront ajoutés à la{' '}
                <span className="font-semibold">
                  {destination === 'queue' ? 'file d\'attente de la partie active' : 'playlist principale de la partie active'}
                </span>
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={uploading}
            className="border-sunset-orange/30 text-sunset-orange hover:bg-purple-500/10"
          >
            Annuler
          </Button>
          <Button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="bg-gradient-to-r from-sunset-orange to-sunset-secondary hover:from-purple-700 hover:to-blue-700"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Upload en cours...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter {files.length > 0 ? `(${files.length})` : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddTrackDialog;
